import { t, type MessageKey } from "../i18n";

/**
 * Everything Settings holds, for the search in its menu: each item and the blocks inside it, with
 * the words people use for them. `anchor` is the id of the block's heading on the item's screen.
 */
export interface SettingsEntry {
  id: string;
  section: string;
  anchor?: string;
  label: MessageKey;
}

export const SETTINGS_ENTRIES: SettingsEntry[] = [
  { id: "numbers", section: "numbers", label: "settings.numbers" },
  { id: "templates", section: "templates", label: "settings.templates" },
  { id: "marketing", section: "marketing", label: "settings.marketing" },
  {
    id: "optout",
    section: "marketing",
    anchor: "opt-out",
    label: "optout.title",
  },
  {
    id: "frequency",
    section: "marketing",
    anchor: "marketing-frequency",
    label: "costs.settings.frequency",
  },
  {
    id: "approval",
    section: "marketing",
    anchor: "campaign-approval",
    label: "costs.settings.approval",
  },
  {
    id: "spenders",
    section: "marketing",
    anchor: "who-can-send",
    label: "marketing.who",
  },
  { id: "members", section: "team", anchor: "people", label: "team.members" },
  { id: "teams", section: "team", anchor: "teams", label: "team.teams" },
  { id: "hours", section: "hours", label: "settings.hours" },
  { id: "agendas", section: "agendas", label: "settings.agendas" },
  { id: "agenda_types", section: "agendas", label: "agenda.tab.types" },
  { id: "agenda_notices", section: "agendas", label: "agenda.tab.notices" },
  { id: "my_calendar", section: "my-calendar", label: "settings.my_calendar" },
  {
    id: "my_calendar_connect",
    section: "my-calendar",
    anchor: "connect",
    label: "mycal.connect.title",
  },
  { id: "outcomes", section: "outcomes", label: "settings.outcomes" },
  { id: "tags", section: "tags", label: "settings.tags" },
  { id: "snippets", section: "snippets", label: "settings.snippets" },
  { id: "fields", section: "fields", label: "settings.fields" },
  { id: "policies", section: "policies", label: "settings.policies" },
  {
    id: "reply_wait",
    section: "policies",
    anchor: "reply-wait",
    label: "replies.title",
  },
  { id: "assistants", section: "assistants", label: "settings.assistants" },
  { id: "skills", section: "skills", label: "settings.skills" },
  { id: "tools", section: "tools", label: "settings.tools" },
  { id: "knowledge", section: "knowledge", label: "settings.knowledge" },
  { id: "learning", section: "learning", label: "settings.learning" },
  { id: "ai_keys", section: "ai", anchor: "ai-keys", label: "ai.keys" },
  { id: "ai_usage", section: "ai", anchor: "ai-usage", label: "ai.usage" },
  { id: "plan", section: "plan", label: "settings.plan" },
  {
    id: "budgets",
    section: "costs",
    anchor: "budgets",
    label: "costs.settings.budgets",
  },
  {
    id: "ai_limit",
    section: "costs",
    anchor: "ai-limit",
    label: "costs.settings.ai_limit",
  },
  {
    id: "limit",
    section: "costs",
    anchor: "limit",
    label: "costs.settings.limit",
  },
  {
    id: "cost_alerts",
    section: "costs",
    anchor: "alerts",
    label: "costs.settings.alerts",
  },
  {
    id: "insights",
    section: "costs",
    anchor: "template-insights",
    label: "costs.settings.insights",
  },
  {
    id: "api_keys",
    section: "integrations",
    anchor: "api-keys",
    label: "integrations.api_keys",
  },
  {
    id: "hooks",
    section: "integrations",
    anchor: "lead-intake",
    label: "integrations.hooks",
  },
  {
    id: "webhooks",
    section: "integrations",
    anchor: "webhooks",
    label: "integrations.webhooks",
  },
  { id: "crm", section: "integrations", anchor: "crm", label: "crm.title" },
  { id: "import", section: "import", label: "settings.import" },
  {
    id: "import-copiloto",
    section: "import",
    anchor: "copiloto",
    label: "import.copiloto.title",
  },
  {
    id: "retention",
    section: "privacy",
    anchor: "retention",
    label: "privacy.retention.title",
  },
  {
    id: "export",
    section: "privacy",
    anchor: "export",
    label: "privacy.export.title",
  },
  {
    id: "support",
    section: "privacy",
    anchor: "support",
    label: "support.title",
  },
  {
    id: "subjects",
    section: "privacy",
    anchor: "data-subjects",
    label: "privacy.subjects.title",
  },
];

/** Lower case without accents: "Horário" and "horario" read the same. */
export const fold = (v: string) =>
  v.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

/**
 * The entries that have every word typed, in their name, their item's name or their synonyms
 * (settings.find.<id>). Matches in the name come first.
 */
export function searchSettings(
  query: string,
  sectionLabel: (section: string) => string,
): SettingsEntry[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const scored = SETTINGS_ENTRIES.map((e) => {
    const name = fold(t(e.label));
    const hay = `${name} ${fold(sectionLabel(e.section))} ${fold(t(`settings.find.${e.id}` as MessageKey))}`;
    if (!words.every((w) => hay.includes(w))) return null;
    return { e, score: words.every((w) => name.includes(w)) ? 0 : 1 };
  }).filter((x): x is { e: SettingsEntry; score: number } => x !== null);
  return scored.sort((a, b) => a.score - b.score).map((x) => x.e);
}
